// DashboardPrototypes.js - Navigation hub for UX overhaul prototypes
// Renders entirely from _manifest.js (PROTOTYPE_MANIFEST): a family tab/filter bar,
// an Enhance/Rethink kind filter, and a collapsible <section> per family with tiles
// carrying a kind badge + effect tags. The 4 legacy themes stay as theme-switcher data
// (LEGACY_THEMES); the 10 legacy prototype tiles were migrated into the manifest losslessly.
//
// Each tile is a real new-tab <a target="_blank" rel="noopener noreferrer"> (WP2) —
// keyboard- and middle-click-friendly; no same-window navigate().

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAssets } from '../../services/assetService';
import {
    PROTOTYPE_MANIFEST,
    FAMILIES,
    FAMILY_LABELS,
    LEGACY_THEMES,
} from './_manifest';
import './DashboardPrototypes.css';

// Families that render as prototype-tile sections (themes is switcher-only).
const TILE_FAMILIES = FAMILIES.filter((f) => f !== 'themes');

function DashboardPrototypes() {
    const [activeTheme, setActiveTheme] = useState(() => {
        return localStorage.getItem('driftalarm-theme') || 'arctic-frost';
    });
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedbackData, setFeedbackData] = useState({
        variation: '',
        rating: 0,
        missing: '',
        confusing: ''
    });

    // Gallery filters (manifest-driven).
    const [familyFilter, setFamilyFilter] = useState('all'); // 'all' | a FAMILIES id
    const [kindFilter, setKindFilter] = useState('all');      // 'all' | 'enhance' | 'rethink'
    // Per-family collapse state (default open).
    const [collapsed, setCollapsed] = useState({});

    // Asset selection state for dossier prototypes
    const [assets, setAssets] = useState([]);
    const [selectedAssetId, setSelectedAssetId] = useState('');
    const [assetsLoading, setAssetsLoading] = useState(true);

    // Fetch assets on mount (the one piece of live data — the dossier asset-selector).
    // Unauthenticated/headless this fails gracefully (logged, not thrown).
    useEffect(() => {
        const fetchAssets = async () => {
            try {
                setAssetsLoading(true);
                const response = await getAssets();
                const assetList = response.assets || response || [];
                if (assetList.length > 0) {
                    setAssets(assetList);
                    setSelectedAssetId(assetList[0].assetId);
                }
            } catch (error) {
                console.log('Failed to fetch assets:', error.message);
            } finally {
                setAssetsLoading(false);
            }
        };

        fetchAssets();
    }, []);

    // Apply theme to document
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', activeTheme);
        localStorage.setItem('driftalarm-theme', activeTheme);
    }, [activeTheme]);

    // Handle theme change with transition
    const handleThemeChange = (themeId) => {
        document.documentElement.classList.add('theme-transitioning');
        setActiveTheme(themeId);
        setTimeout(() => {
            document.documentElement.classList.remove('theme-transitioning');
        }, 300);
    };

    // Group + filter the manifest by family, honoring the kind filter.
    const sections = useMemo(() => {
        return TILE_FAMILIES
            .filter((family) => familyFilter === 'all' || familyFilter === family)
            .map((family) => {
                const tiles = PROTOTYPE_MANIFEST.filter(
                    (e) => e.family === family && (kindFilter === 'all' || e.kind === kindFilter)
                );
                return { family, label: FAMILY_LABELS[family] || family, tiles };
            })
            .filter((s) => s.tiles.length > 0);
    }, [familyFilter, kindFilter]);

    // When the "Themes" family tab is active, hide prototype sections (theme switcher covers it).
    const showSections = familyFilter !== 'themes';

    const toggleSection = (family) => {
        setCollapsed((prev) => ({ ...prev, [family]: !prev[family] }));
    };

    // Build the new-tab/navigate href for an entry (dossiers append the selected asset id).
    const hrefFor = (entry) => {
        if (entry.family === 'dossiers') {
            return selectedAssetId ? `${entry.route}/${selectedAssetId}` : null;
        }
        return entry.route;
    };

    // Handle feedback submission
    const handleFeedbackSubmit = (e) => {
        e.preventDefault();
        console.log('Feedback submitted:', feedbackData);
        // TODO: Store feedback to API
        setShowFeedback(false);
        setFeedbackData({ variation: '', rating: 0, missing: '', confusing: '' });
    };

    return (
        <div className="prototypes-page">
            {/* Header */}
            <header className="prototypes-header">
                <div className="header-content">
                    <Link to="/command-center" className="back-link">
                        ← Back to Command Center
                    </Link>
                    <h1 className="page-title">UX Overhaul Prototypes</h1>
                    <p className="page-subtitle">
                        Explore dashboard variations, design spikes, and theme options for the DriftAlarm redesign
                    </p>
                </div>
            </header>

            {/* Theme Selector (legacy themes; always visible) */}
            <section className="theme-section">
                <h2 className="section-title">Theme Variations</h2>
                <p className="section-description">
                    Select a theme to preview across all prototypes. Theme preference is saved.
                </p>

                <div className="theme-grid">
                    {LEGACY_THEMES.map((theme) => (
                        <button
                            key={theme.id}
                            className={`theme-card ${activeTheme === theme.id ? 'active' : ''}`}
                            onClick={() => handleThemeChange(theme.id)}
                        >
                            <div
                                className="theme-preview"
                                style={{
                                    background: theme.colors.bg,
                                    borderColor: theme.colors.border
                                }}
                            >
                                <div
                                    className="theme-accent-bar"
                                    style={{ background: theme.colors.accent }}
                                />
                                <div className="theme-preview-content">
                                    <div className="preview-line" style={{ background: theme.colors.border }} />
                                    <div className="preview-line short" style={{ background: theme.colors.border }} />
                                </div>
                            </div>
                            <div className="theme-info">
                                <span className="theme-name">{theme.name}</span>
                                <span className="theme-desc">{theme.description}</span>
                            </div>
                            {activeTheme === theme.id && (
                                <span className="theme-active-badge">Active</span>
                            )}
                        </button>
                    ))}
                </div>
            </section>

            {/* Filter bar: family tabs + Enhance/Rethink kind filter */}
            <section className="gallery-controls">
                <div className="filter-row">
                    <span className="filter-label">Family</span>
                    <div className="family-tabs" role="tablist" aria-label="Prototype family filter">
                        <button
                            className={`family-tab ${familyFilter === 'all' ? 'active' : ''}`}
                            role="tab"
                            aria-selected={familyFilter === 'all'}
                            onClick={() => setFamilyFilter('all')}
                        >
                            All
                        </button>
                        {FAMILIES.map((family) => (
                            <button
                                key={family}
                                className={`family-tab ${familyFilter === family ? 'active' : ''}`}
                                role="tab"
                                aria-selected={familyFilter === family}
                                onClick={() => setFamilyFilter(family)}
                            >
                                {FAMILY_LABELS[family] || family}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="filter-row">
                    <span className="filter-label">Kind</span>
                    <div className="kind-filter" role="tablist" aria-label="Prototype kind filter">
                        {['all', 'enhance', 'rethink'].map((k) => (
                            <button
                                key={k}
                                className={`kind-pill ${kindFilter === k ? 'active' : ''} kind-pill--${k}`}
                                role="tab"
                                aria-selected={kindFilter === k}
                                onClick={() => setKindFilter(k)}
                            >
                                {k === 'all' ? 'All' : k === 'enhance' ? 'Enhance' : 'Rethink'}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Asset selector (drives dossier-tile hrefs) */}
            {showSections && (
                <section className="asset-selector-section">
                    <div className="asset-selector">
                        <label className="asset-selector-label">
                            <span>Select Asset (for Asset Dossier tiles):</span>
                            {assetsLoading ? (
                                <span className="asset-loading">Loading assets...</span>
                            ) : assets.length === 0 ? (
                                <span className="asset-empty">No assets found. <Link to="/assets">Add an asset first</Link></span>
                            ) : (
                                <select
                                    value={selectedAssetId}
                                    onChange={(e) => setSelectedAssetId(e.target.value)}
                                    className="asset-select"
                                >
                                    {assets.map((asset) => (
                                        <option key={asset.assetId} value={asset.assetId}>
                                            {asset.value || asset.target} ({asset.assetType || 'domain'})
                                        </option>
                                    ))}
                                </select>
                            )}
                        </label>
                    </div>
                </section>
            )}

            {/* Manifest-driven collapsible family sections */}
            {showSections && sections.map(({ family, label, tiles }) => {
                const isOpen = !collapsed[family];
                return (
                    <section className="variations-section family-section" key={family} data-family={family}>
                        <button
                            className="family-section-header"
                            onClick={() => toggleSection(family)}
                            aria-expanded={isOpen}
                        >
                            <span className={`family-chevron ${isOpen ? 'open' : ''}`} aria-hidden="true">▸</span>
                            <h2 className="section-title family-section-title">{label}</h2>
                            <span className="family-count">{tiles.length}</span>
                        </button>

                        {isOpen && (
                            <div className="variations-grid">
                                {tiles.map((entry) => {
                                    const href = hrefFor(entry);
                                    const isDisabled = entry.status !== 'ready' || href === null;
                                    // Tile content is identical whether enabled (new-tab <a>) or
                                    // disabled (plain <div>, no href — the "select an asset first" state).
                                    const inner = (
                                        <>
                                            <div className="variation-header">
                                                <span className="variation-id">{entry.id.toUpperCase()}</span>
                                                <span className={`kind-badge kind-badge--${entry.kind}`}>
                                                    {entry.kind === 'enhance' ? 'Enhance' : 'Rethink'}
                                                </span>
                                            </div>

                                            <h3 className="variation-name">{entry.name}</h3>
                                            <p className="variation-description">{entry.description}</p>

                                            {entry.effects.length > 0 && (
                                                <ul className="variation-features effect-tags">
                                                    {entry.effects.map((fx) => (
                                                        <li key={fx} className="effect-tag">{fx}</li>
                                                    ))}
                                                </ul>
                                            )}

                                            {!isDisabled && (
                                                <span className="variation-cta">Open in new tab →</span>
                                            )}
                                            {entry.family === 'dossiers' && href === null && (
                                                <span className="variation-cta disabled">Select an asset first</span>
                                            )}
                                        </>
                                    );

                                    if (isDisabled) {
                                        return (
                                            <div key={entry.id} className="variation-card pending">
                                                {inner}
                                            </div>
                                        );
                                    }
                                    return (
                                        <a
                                            key={entry.id}
                                            className="variation-card"
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {inner}
                                        </a>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                );
            })}

            {/* Feedback Section */}
            <section className="feedback-section">
                <h2 className="section-title">Provide Feedback</h2>
                <p className="section-description">
                    Your input helps us choose the best design direction.
                </p>

                <button
                    className="feedback-trigger"
                    onClick={() => setShowFeedback(true)}
                >
                    📝 Open Feedback Form
                </button>
            </section>

            {/* Feedback Modal */}
            {showFeedback && (
                <div className="feedback-modal-overlay" onClick={() => setShowFeedback(false)}>
                    <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="modal-close"
                            onClick={() => setShowFeedback(false)}
                        >
                            ✕
                        </button>

                        <h3>Design Feedback</h3>

                        <form onSubmit={handleFeedbackSubmit}>
                            <label className="form-field">
                                <span>Which prototype did you review?</span>
                                <select
                                    value={feedbackData.variation}
                                    onChange={(e) => setFeedbackData(prev => ({
                                        ...prev,
                                        variation: e.target.value
                                    }))}
                                    required
                                >
                                    <option value="">Select...</option>
                                    {PROTOTYPE_MANIFEST.map((entry) => (
                                        <option key={entry.id} value={entry.id}>{entry.name}</option>
                                    ))}
                                </select>
                            </label>

                            <label className="form-field">
                                <span>Rating (1-5)</span>
                                <div className="rating-stars">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            className={`star ${feedbackData.rating >= star ? 'active' : ''}`}
                                            onClick={() => setFeedbackData(prev => ({
                                                ...prev,
                                                rating: star
                                            }))}
                                        >
                                            ★
                                        </button>
                                    ))}
                                </div>
                            </label>

                            <label className="form-field">
                                <span>What's missing?</span>
                                <textarea
                                    value={feedbackData.missing}
                                    onChange={(e) => setFeedbackData(prev => ({
                                        ...prev,
                                        missing: e.target.value
                                    }))}
                                    placeholder="Features or information you expected to see..."
                                    rows={3}
                                />
                            </label>

                            <label className="form-field">
                                <span>What's confusing?</span>
                                <textarea
                                    value={feedbackData.confusing}
                                    onChange={(e) => setFeedbackData(prev => ({
                                        ...prev,
                                        confusing: e.target.value
                                    }))}
                                    placeholder="Anything unclear or hard to understand..."
                                    rows={3}
                                />
                            </label>

                            <button type="submit" className="submit-btn">
                                Submit Feedback
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DashboardPrototypes;
